/**
 * Qué cara de la app se muestra.
 *
 * El flag del dominio (trackvision.suiteUi) habilita el interruptor.
 * No se guarda preferencia. Al iniciar sesión la cara es la clásica y el
 * login abre el intro clásico. El interruptor, ya dentro, cambia solo esta
 * visita (sessionStorage) y se pierde al cerrar sesión. La suite no tiene intro.
 */

export type Cara = 'classic' | 'suite';

/** Quedó de cuando se podía guardar la cara. No se lee. El logout la borra. */
export const CLAVE_CARA_GUARDADA = 'inprovider.suiteCara';
export const CLAVE_CARA_SESION = 'inprovider.suiteCaraSesion';

export function suiteUiActivo(flag: unknown): boolean {
  return flag === true;
}

function esCara(valor: unknown): valor is Cara {
  return valor === 'classic' || valor === 'suite';
}

export function caraAlEntrar(flag: unknown, deSesion: unknown): Cara {
  if (!suiteUiActivo(flag)) return 'classic';
  if (esCara(deSesion)) return deSesion;
  return 'classic';
}

export function etiquetaInterruptor(cara: Cara): string {
  return cara === 'suite' ? 'Cambiar a interfaz clásica' : 'Conocer la nueva interfaz';
}

/** A dónde va el interruptor cuando no hay una funcionalidad abierta. */
export function entradaDeCara(cara: Cara): string {
  return cara === 'suite' ? '/notificaciones' : '/flows/requirements';
}

/** `?suite=` que pide al sidebar abrir el primer módulo con flujos. */
export const SUITE_PRIMER_MODULO = 'primero';

/**
 * Entrada a la suite sin una funcionalidad abierta. Notificaciones solo si el
 * dominio la tiene (myactivities), igual que en la clásica; si no, el primer
 * módulo con flujos.
 */
export function entradaSuite(notificaciones: boolean): string {
  return notificaciones ? '/notificaciones' : `/flows/requirements?suite=${SUITE_PRIMER_MODULO}`;
}

/** Un panel de notificaciones anotado no vale en un dominio que no las tiene. */
export function seleccionVigente(
  seleccion: SeleccionClasica | null,
  notificaciones: boolean,
): SeleccionClasica | null {
  if (!seleccion || notificaciones || seleccion.panel !== 'notificaciones') return seleccion;
  return { ...seleccion, panel: null };
}

/**
 * En la clásica, calendario y notificaciones viven en la lista de
 * requerimientos. En la suite cada uno tiene ruta propia.
 */
export const CLAVE_PANEL_CLASICO = 'inprovider.panelClasico';
export const CLAVE_GRUPO_FLUJO = 'id_visibility_req_menu';
export const CLAVE_VISIBILIDAD_FLUJO = 'visibility_requirement_list';

export type PanelClasico = 'calendario' | 'notificaciones';

export function panelClasicoDeRuta(url: string): PanelClasico | null {
  const path = (url || '').split('?')[0];
  if (path === '/calendario') return 'calendario';
  if (path === '/notificaciones') return 'notificaciones';
  return null;
}

export function leerPanelClasico(leer: (clave: string) => string | null): PanelClasico | null {
  const panel = leer(CLAVE_PANEL_CLASICO);
  return panel === 'calendario' || panel === 'notificaciones' ? panel : null;
}

export interface SeleccionClasica {
  panel: PanelClasico | null;
  grupo: number | null;
  visibilidad: number | null;
}

export function leerSeleccionClasica(leer: (clave: string) => string | null): SeleccionClasica {
  const num = (clave: string) => {
    const raw = leer(clave);
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };
  return {
    panel: leerPanelClasico(leer),
    grupo: num(CLAVE_GRUPO_FLUJO),
    visibilidad: num(CLAVE_VISIBILIDAD_FLUJO),
  };
}

/** La grilla de un flujo en la suite: /flows/requirements/g/:grupo/v/:etapa. */
export function flujoDeRuta(url: string): { grupo: number; visibilidad: number } | null {
  const path = (url || '').split('?')[0];
  const flujo = /^\/flows\/requirements\/g\/(\d+)\/v\/(\d+)$/.exec(path);
  if (!flujo) return null;
  return { grupo: Number(flujo[1]), visibilidad: Number(flujo[2]) };
}

/** -1 calendario, 0 notificaciones, null = el default de la lista. */
export function grupoAlAbrirLista(panel: PanelClasico | null): -1 | 0 | null {
  if (panel === 'calendario') return -1;
  if (panel === 'notificaciones') return 0;
  return null;
}

/**
 * Qué abre la lista clásica. El panel (calendario o notificaciones) gana
 * sobre un flujo que haya quedado anotado.
 */
export function aperturaDeLista(
  seleccion: SeleccionClasica | null,
): { visibilityGroup: number; visibilityId: number } | null {
  const grupoPanel = grupoAlAbrirLista(seleccion?.panel ?? null);
  if (grupoPanel != null) return { visibilityGroup: grupoPanel, visibilityId: grupoPanel };
  const grupo = seleccion?.grupo;
  const visibilidad = seleccion?.visibilidad;
  if (grupo != null && visibilidad != null && grupo > 0 && visibilidad > 0) {
    return { visibilityGroup: grupo, visibilityId: visibilidad };
  }
  return null;
}

export function escribirPanelClasico(
  escribir: (clave: string, valor: string | null) => void,
  panel: PanelClasico | null,
): void {
  escribir(CLAVE_PANEL_CLASICO, panel);
  if (panel) {
    escribir(CLAVE_GRUPO_FLUJO, null);
    escribir(CLAVE_VISIBILIDAD_FLUJO, null);
  }
}

/** Al volver de un flujo de la suite, la lista clásica abre ese grupo y etapa. */
export function escribirFlujoClasico(
  escribir: (clave: string, valor: string | null) => void,
  grupo: number,
  visibilidad: number,
): void {
  escribir(CLAVE_GRUPO_FLUJO, String(grupo));
  escribir(CLAVE_VISIBILIDAD_FLUJO, String(visibilidad));
  escribir(CLAVE_PANEL_CLASICO, null);
}

/**
 * Lo que muestra la lista clásica, anotado para el interruptor: el panel
 * (calendario / notificaciones) o el flujo y su etapa, aunque se hayan abierto
 * solos al cargar la lista.
 */
export function anotarListaClasica(
  escribir: (clave: string, valor: string | null) => void,
  grupo: number | null | undefined,
  visibilidad: number | null | undefined,
): void {
  const panel = grupo === -1 ? 'calendario' : grupo === 0 ? 'notificaciones' : null;
  if (panel) {
    escribirPanelClasico(escribir, panel);
    return;
  }
  const g = Number(grupo);
  const v = Number(visibilidad);
  if (g > 0 && v > 0) escribirFlujoClasico(escribir, g, v);
  else escribirPanelClasico(escribir, null);
}

/**
 * La misma pantalla en la otra cara.
 * Calendario y notificaciones, en la clásica, son paneles de la lista.
 * Un flujo abierto pasa a /flows/requirements/g/:grupo/v/:etapa.
 * Services cambia de prefijo. El resto de rutas se comparte.
 * `entrada` es a dónde va la suite cuando no hay nada que conservar.
 */
export function mismaFuncionalidad(
  destino: Cara,
  url: string,
  seleccion: SeleccionClasica | null = null,
  entrada = '/notificaciones',
): string {
  const services = canonServices(destino, url);
  if (services) return services;

  const [path, query = ''] = (url || '').split('?');
  const cola = query ? `?${query}` : '';
  const flujo = flujoDeRuta(path);

  if (destino === 'classic') {
    if (panelClasicoDeRuta(path) || flujo) return '/flows/requirements';
    if (path === '/intro') return '/flows/requirements';
    return path + cola;
  }

  if (flujo) return path + cola;
  if (path === '/flows/requirements') {
    if (seleccion?.panel === 'calendario') return '/calendario';
    if (seleccion?.panel === 'notificaciones') return '/notificaciones';
    const abierto = aperturaDeLista(seleccion);
    if (abierto && abierto.visibilityGroup > 0) {
      return `/flows/requirements/g/${abierto.visibilityGroup}/v/${abierto.visibilityId}`;
    }
    return entrada;
  }
  if (path === '/intro') return entrada;
  return path + cola;
}

/** Tras el login: el intro clásico. La suite no reemplaza esa pantalla. */
export function entradaTrasLogin(): string {
  return '/intro';
}

/** La lista clásica vive en /services. La suite usa /flows/contratos. */
export function rutaServices(cara: Cara, segmento = ''): string {
  const base = cara === 'suite' ? '/flows/contratos' : '/services';
  return segmento ? `${base}/${segmento}` : base;
}

const PREFIJO_SERVICES_SUITE = '/flows/contratos';
const PREFIJO_SERVICES_CLASSIC = '/services';

/**
 * null = la URL ya es la de esta cara. Si no, la misma pantalla en el prefijo
 * de la cara (conserva el resto del path y la query).
 */
export function canonServices(cara: Cara, url: string): string | null {
  const [path, query = ''] = url.split('?');
  const cola = query ? `?${query}` : '';
  if (cara === 'classic' && (path === PREFIJO_SERVICES_SUITE || path.startsWith(`${PREFIJO_SERVICES_SUITE}/`))) {
    return PREFIJO_SERVICES_CLASSIC + path.slice(PREFIJO_SERVICES_SUITE.length) + cola;
  }
  if (cara === 'suite' && (path === PREFIJO_SERVICES_CLASSIC || path.startsWith(`${PREFIJO_SERVICES_CLASSIC}/`))) {
    return PREFIJO_SERVICES_SUITE + path.slice(PREFIJO_SERVICES_CLASSIC.length) + cola;
  }
  return null;
}
